import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

// Store the token getter function
let getAuthToken = null;
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to set the auth token getter
export const setAuthTokenGetter = (tokenGetter) => {
  getAuthToken = tokenGetter;
};

// Create axios instance with timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 second timeout
});

// Helper to get token with retry
const getTokenWithRetry = async (retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) return token;
      }
      // Wait a bit before retrying if token is null
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
      }
    } catch (error) {
      console.error(`Token fetch attempt ${i + 1} failed:`, error);
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)));
      }
    }
  }
  return null;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // For dashboard API calls, add Clerk session token
    const token = await getTokenWithRetry();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling with retry logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If no response (network error), retry once
    if (!error.response && !originalRequest._retry) {
      originalRequest._retry = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      return api(originalRequest);
    }

    // Handle 401 errors - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to get a fresh token
        const newToken = await getTokenWithRetry();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return api(originalRequest);
        } else {
          processQueue(new Error('Failed to refresh token'), null);
          // Only redirect if we truly can't authenticate
          window.location.href = '/sign-in';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        window.location.href = '/sign-in';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For 500 errors, retry once
    if (error.response?.status >= 500 && !originalRequest._retryServer) {
      originalRequest._retryServer = true;
      await new Promise(resolve => setTimeout(resolve, 1000));
      return api(originalRequest);
    }

    return Promise.reject(error);
  }
);

// API Key Management
export const apiKeyService = {
  create: (data) => api.post('/api/key', data),
  getAll: () => api.get('/api/key'),
  revoke: (keyId) => api.delete(`/api/key/${keyId}`),
  reset: (keyId) => api.post(`/api/key/${keyId}/reset`),
  update: (keyId, data) => api.put(`/api/key/${keyId}`, data)
};

// Usage & Analytics
export const usageService = {
  getStats: (params) => api.get('/api/usage/stats', { params }),
  getOverview: () => api.get('/api/usage/overview'),
  getLogs: (params) => api.get('/api/logs', { params })
};

// Billing & Subscription
export const billingService = {
  getSubscription: () => api.get('/api/payment/subscription'),
  getPaymentHistory: () => api.get('/api/payment/history'),
  cancelSubscription: () => api.post('/api/payment/cancel-subscription')
};

// Email Notification Preferences
export const notificationService = {
  getPreferences: () => api.get('/api/notifications/preferences'),
  updatePreferences: (data) => api.put('/api/notifications/preferences', data),
  resetPreferences: () => api.post('/api/notifications/preferences/reset'),
  sendTestEmail: (emailType) => api.post('/api/notifications/test-email', { emailType })
};

// Admin
export const adminService = {
  getStats: () => api.get('/api/admin/stats'),
  getUsers: (params) => api.get('/api/admin/users', { params }),
  getUserDetails: (userId) => api.get(`/api/admin/users/${userId}`),
  updateUser: (userId, data) => api.put(`/api/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/api/admin/users/${userId}`)
};

// Public API (with API key) - All endpoints
export const publicApi = {
  // Single image analysis (file or URL)
  analyzeSingle: (data, apiKey) => {
    const formData = new FormData();
    
    if (data.file) {
      formData.append('file', data.file);
    } else if (data.url) {
      formData.append('url', data.url);
    }

    return axios.post(`${API_BASE_URL}/api/analyze/single`, formData, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  // Multi image analysis (file uploads)
  analyzeMulti: (files, apiKey) => {
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('files', file);
    });

    return axios.post(`${API_BASE_URL}/api/analyze/multi`, formData, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // URL-based analysis (array of URLs)
  analyzeUrls: (urls, apiKey) => {
    return axios.post(`${API_BASE_URL}/api/analyze/urls`, 
      { urls },
      {
        headers: {
          'X-API-Key': apiKey,
          'Content-Type': 'application/json'
        }
      }
    );
  },

  // Streaming analysis for large batches
  analyzeStreaming: async (files, apiKey, onProgress) => {
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await fetch(`${API_BASE_URL}/api/analyze/streaming`, {
      method: 'POST',
      headers: {
        'X-API-Key': apiKey
      },
      body: formData
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const results = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const text = decoder.decode(value);
      const lines = text.split('\n');
      
      lines.forEach(line => {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            results.push(data);
            if (onProgress) onProgress(data);
          } catch (e) {
            // Skip invalid JSON
          }
        }
      });
    }

    return results;
  },

  // Get API health status
  getHealth: (apiKey) => {
    return axios.get(`${API_BASE_URL}/api/analyze/health`, {
      headers: apiKey ? { 'X-API-Key': apiKey } : {}
    });
  },

  // Get API configuration
  getConfig: (apiKey) => {
    return axios.get(`${API_BASE_URL}/api/analyze/config`, {
      headers: apiKey ? { 'X-API-Key': apiKey } : {}
    });
  },

  // Legacy endpoints for backwards compatibility
  analyze: (data, apiKey) => {
    const formData = new FormData();
    
    if (data.file) {
      formData.append('image', data.file);
    } else if (data.url) {
      formData.append('url', data.url);
    }

    return axios.post(`${API_BASE_URL}/api/analyze/analyze`, formData, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  
  batch: (data, apiKey) => {
    const formData = new FormData();
    
    if (data.files) {
      data.files.forEach(file => {
        formData.append('images', file);
      });
    } else if (data.urls) {
      formData.append('urls', JSON.stringify(data.urls));
    }

    return axios.post(`${API_BASE_URL}/api/analyze/batch`, formData, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'multipart/form-data'
      }
    });
  }
};

export default api;
